import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import parse from "html-react-parser";

import Loader from "../Loader";
import { sanitizeHtml } from "../../utils/sanitize";
import { questionImageUrl } from "../../utils/assets";
import { getRevisionData } from "./../../actions/resultActions";

import "./ResultView.css";

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const STATUS_LABELS = {
  correct: "Correct",
  incorrect: "Incorrect",
  skipped: "Skipped",
};

function renderHtml(value) {
  if (!value) return null;
  return parse(sanitizeHtml(String(value)));
}

function toPercent(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

const SubjectBar = ({ correct, incorrect, skipped, total }) => (
  <div className="revision__bar">
    <span
      className="revision__bar-seg is-correct"
      style={{ width: `${toPercent(correct, total)}%` }}
    />
    <span
      className="revision__bar-seg is-incorrect"
      style={{ width: `${toPercent(incorrect, total)}%` }}
    />
    <span
      className="revision__bar-seg is-skipped"
      style={{ width: `${toPercent(skipped, total)}%` }}
    />
  </div>
);

const ResultView = ({ match }) => {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { revLoading, revision } = useSelector((state) => state.results);
  const quizId = match.params.quizId;

  const [subjectIndex, setSubjectIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [explanationOpen, setExplanationOpen] = useState(false);

  useEffect(() => {
    dispatch(getRevisionData(quizId));
  }, [dispatch, quizId]);

  const subjects = useMemo(() => {
    const rows = Array.isArray(revision?.subjects) ? revision.subjects : [];

    return rows.map((row) => {
      const total = Number(row.totalQuestions) || 0;
      return {
        id: row.subject?._id,
        title: row.subject?.title || "Subject",
        abbr: (row.subject?.title || "SUB").replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase(),
        total,
        correct: Number(row.correct) || 0,
        incorrect: Number(row.incorrect) || 0,
        skipped: Number(row.skipped) || 0,
        accuracy: toPercent(Number(row.correct) || 0, total),
        questions: Array.isArray(row.questions) ? row.questions : [],
      };
    });
  }, [revision]);

  const totals = useMemo(
    () =>
      subjects.reduce(
        (acc, subject) => ({
          correct: acc.correct + subject.correct,
          incorrect: acc.incorrect + subject.incorrect,
          skipped: acc.skipped + subject.skipped,
        }),
        { correct: 0, incorrect: 0, skipped: 0 }
      ),
    [subjects]
  );

  useEffect(() => {
    if (subjects.length === 0) return;
    const firstAnswered = subjects.findIndex((subject) => subject.questions.length > 0);
    setSubjectIndex(firstAnswered >= 0 ? firstAnswered : 0);
    setQuestionIndex(0);
  }, [subjects.length]);

  useEffect(() => {
    setExplanationOpen(false);
  }, [subjectIndex, questionIndex]);

  const activeSubject = subjects[subjectIndex];
  const questions = activeSubject?.questions || [];
  const activeQuestion = questions[questionIndex];

  const studentName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const attemptTitle = [revision?.quiz?.exam?.title, revision?.quiz?.assessment?.title]
    .filter(Boolean)
    .join(" ");

  const goToSubject = (index) => {
    setSubjectIndex(index);
    setQuestionIndex(0);
  };

  if (revLoading !== false) return <Loader />;

  if (!revision?.quiz) {
    return (
      <div className="revision revision--empty">
        <h2>Revision not available</h2>
        <p>We could not load this attempt for review.</p>
        <Link className="revision__retake" to="/results">
          Back to results
        </Link>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="revision">
        <div className="revision__topbar">
          <Link className="revision__back" to={`/practice-results/${quizId}`}>
            <i className="fas fa-arrow-left" /> Back to Results
          </Link>

          <div className="revision__heading">
            <p className="revision__kicker">Revision Review</p>
            <h1 className="revision__title">
              {attemptTitle}
              {studentName ? ` — ${studentName}` : ""}
            </h1>
          </div>

          <div className="revision__counters">
            <span className="revision__counter is-correct">
              <i className="fas fa-check-circle" /> {totals.correct} Correct
            </span>
            <span className="revision__counter is-incorrect">
              <i className="fas fa-times-circle" /> {totals.incorrect} Incorrect
            </span>
            <span className="revision__counter is-skipped">
              <i className="fas fa-minus-circle" /> {totals.skipped} Skipped
            </span>
            <Link className="revision__retake" to="/mode-of-assessment">
              <i className="fas fa-redo" /> Retake Exam
            </Link>
          </div>
        </div>

        <div className="revision__body">
          <div className="revision__sidebar">
            <div className="revision__user">
              <span className="revision__avatar">{initials || "ME"}</span>
              <div className="revision__user-meta">
                <p className="revision__user-name">{studentName || "Candidate"}</p>
                <span className="revision__user-sub">{attemptTitle}</span>
              </div>
            </div>

            <p className="revision__label">Subjects</p>
            <ul className="revision__subjects">
              {subjects.map((subject, index) => (
                <li key={subject.id || index}>
                  <button
                    type="button"
                    className={`revision__subject${index === subjectIndex ? " is-active" : ""}`}
                    onClick={() => goToSubject(index)}
                  >
                    <span className="revision__subject-top">
                      <span className="revision__subject-badge">{subject.abbr}</span>
                      <span className="revision__subject-name">{subject.title}</span>
                      <span className="revision__subject-count">{subject.total}Q</span>
                    </span>
                    <SubjectBar
                      correct={subject.correct}
                      incorrect={subject.incorrect}
                      skipped={subject.skipped}
                      total={subject.total}
                    />
                    <span className="revision__subject-stats">
                      <span className="is-correct">
                        <i className="fas fa-check-circle" /> {subject.correct}
                      </span>
                      <span className="is-incorrect">
                        <i className="fas fa-times-circle" /> {subject.incorrect}
                      </span>
                      <span className="is-skipped">
                        <i className="fas fa-minus-circle" /> {subject.skipped}
                      </span>
                      <span className="revision__subject-accuracy">{subject.accuracy}%</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="revision__label-row">
              <p className="revision__label">Questions</p>
              <span className="revision__label-count">
                {questions.length === 0 ? 0 : questionIndex + 1} / {questions.length}
              </span>
            </div>

            {questions.length === 0 ? (
              <p className="revision__note">Nothing was answered in this subject.</p>
            ) : (
              <Fragment>
                <div className="revision__grid">
                  {questions.map((question, index) => (
                    <button
                      key={question._id || index}
                      type="button"
                      className={`revision__chip is-${question.status}${
                        index === questionIndex ? " is-active" : ""
                      }`}
                      onClick={() => setQuestionIndex(index)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                {activeSubject.skipped > 0 && (
                  <p className="revision__note">
                    {activeSubject.skipped} skipped question
                    {activeSubject.skipped === 1 ? "" : "s"} are not listed — they were never
                    opened during the exam.
                  </p>
                )}
              </Fragment>
            )}

            <ul className="revision__legend">
              <li>
                <span className="revision__dot is-correct" /> Correct
                <b>{activeSubject?.correct ?? 0}</b>
              </li>
              <li>
                <span className="revision__dot is-incorrect" /> Incorrect
                <b>{activeSubject?.incorrect ?? 0}</b>
              </li>
              <li>
                <span className="revision__dot is-skipped" /> Skipped
                <b>{activeSubject?.skipped ?? 0}</b>
              </li>
            </ul>
          </div>

          <div className="revision__main">
            {!activeQuestion ? (
              <div className="revision__placeholder">
                <span className="revision__placeholder-icon">
                  <i className="fas fa-inbox" />
                </span>
                <h2>No answered questions here</h2>
                <p>
                  You did not answer any {activeSubject?.title} question in this attempt, so
                  there is nothing to revise. Pick another subject on the left.
                </p>
              </div>
            ) : (
              <Fragment>
                <div className="revision__crumbs">
                  <span className="revision__crumb revision__crumb--subject">
                    <i className="fas fa-book" /> {activeSubject.title}
                  </span>
                  {activeQuestion.topic && (
                    <span className="revision__crumb">{activeQuestion.topic}</span>
                  )}
                  <span className={`revision__crumb revision__crumb--${activeQuestion.status}`}>
                    {STATUS_LABELS[activeQuestion.status]}
                  </span>

                  <div className="revision__pager">
                    <span>
                      {questionIndex + 1} / {questions.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuestionIndex(questionIndex - 1)}
                      disabled={questionIndex === 0}
                      aria-label="Previous question"
                    >
                      <i className="fas fa-angle-left" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuestionIndex(questionIndex + 1)}
                      disabled={questionIndex >= questions.length - 1}
                      aria-label="Next question"
                    >
                      <i className="fas fa-angle-right" />
                    </button>
                  </div>
                </div>

                {activeQuestion.passage && (
                  <div className="revision__passage">{renderHtml(activeQuestion.passage)}</div>
                )}

                <div className="revision__question">
                  <div className="revision__question-head">
                    <span className="revision__question-no">{questionIndex + 1}</span>
                    <div className="revision__question-text">
                      {activeQuestion.title && (
                        <div className="revision__question-title">
                          {renderHtml(activeQuestion.title)}
                        </div>
                      )}
                      {renderHtml(activeQuestion.question)}
                    </div>
                  </div>

                  {activeQuestion.logo && (
                    <img
                      className="revision__question-image"
                      src={questionImageUrl(activeQuestion.logo)}
                      alt="Question illustration"
                    />
                  )}

                  <ul className="revision__options">
                    {activeQuestion.options.map((option, index) => {
                      const isCorrect = String(option._id) === activeQuestion.correctId;
                      const isChosen = String(option._id) === activeQuestion.answeredId;
                      const tone = isCorrect ? " is-correct" : isChosen ? " is-chosen" : "";

                      return (
                        <li key={option._id || index} className={`revision__option${tone}`}>
                          <span className="revision__option-letter">
                            {OPTION_LETTERS[index] || index + 1}
                          </span>
                          <div className="revision__option-text">{renderHtml(option.option)}</div>
                          {isChosen && !isCorrect && (
                            <span className="revision__tag is-incorrect">
                              <i className="fas fa-times-circle" /> Your Answer
                            </span>
                          )}
                          {isCorrect && (
                            <span className="revision__tag is-correct">
                              <i className="fas fa-check-circle" />{" "}
                              {isChosen ? "Your Answer — Correct" : "Correct"}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="revision__explanation">
                  <button
                    type="button"
                    className="revision__explanation-head"
                    onClick={() => setExplanationOpen(!explanationOpen)}
                    aria-expanded={explanationOpen}
                  >
                    <span className="revision__explanation-icon">
                      <i className="fas fa-lightbulb" />
                    </span>
                    <span className="revision__explanation-copy">
                      <span className="revision__explanation-title">Explanation &amp; Study Tip</span>
                      <span className="revision__explanation-sub">
                        {explanationOpen
                          ? "Hide the explanation for this question"
                          : "Reveal the explanation for this question"}
                      </span>
                    </span>
                    <i
                      className={`fas fa-chevron-${explanationOpen ? "up" : "down"} revision__chevron`}
                    />
                  </button>
                  {explanationOpen && (
                    <div className="revision__explanation-body">
                      {activeQuestion.explanation ? (
                        renderHtml(activeQuestion.explanation)
                      ) : (
                        <p>
                          No explanation has been published for this question yet.
                          {activeQuestion.subtopic
                            ? ` Revise "${activeQuestion.subtopic}" to strengthen this area.`
                            : ""}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="revision__footer">
                  <button
                    type="button"
                    className="revision__nav"
                    onClick={() => setQuestionIndex(questionIndex - 1)}
                    disabled={questionIndex === 0}
                  >
                    <i className="fas fa-angle-left" /> Previous
                  </button>

                  <div className="revision__dots">
                    {questions.map((question, index) => (
                      <span
                        key={question._id || index}
                        className={`revision__dot is-${question.status}${
                          index === questionIndex ? " is-active" : ""
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    className="revision__nav revision__nav--green"
                    onClick={() => setQuestionIndex(questionIndex + 1)}
                    disabled={questionIndex >= questions.length - 1}
                  >
                    Next <i className="fas fa-angle-right" />
                  </button>
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ResultView;
